import { useState } from 'react';
import toast from 'react-hot-toast';
import { useDrawing } from '../../hooks/useDrawing.js';
import { useMapStore } from '../../stores/mapStore.js';
import { api } from '../../services/api.js';
import { Button, Input, Modal } from '../ui/index.js';
import { ANNOTATION_TOOLS } from './AnnotationLayer.js';
import type { AnnotationType } from '../../types/index.js';

export function DrawingTools() {
  const { mode, pendingFeatures, setDrawMode, saveDrawing, clearAll } = useDrawing();
  const { mapMode, activeAnnotationTool, setActiveAnnotationTool, activeDay } = useMapStore();
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#4361ee');
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);

  if (!mapMode) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await saveDrawing(name, color, 0.4);
    setSaving(false);
    setSaveOpen(false);
    setName('');
  };

  const handleShare = async () => {
    if (!activeDay) return;
    setSharing(true);
    try {
      const result = await api.post<{ token: string; url: string }>(`/days/${activeDay.id}/share`, {});
      if (!result.success) { toast.error('Failed to create share link'); return; }
      const url = window.location.origin + result.data.url;
      await navigator.clipboard.writeText(url);
      toast.success('Map link copied to clipboard!');
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleDrawTool = (m: Parameters<typeof setDrawMode>[0]) => {
    setActiveAnnotationTool(null); // deactivate stamp tool
    setDrawMode(m);
  };

  const handleStampTool = (t: AnnotationType) => {
    const next = activeAnnotationTool === t ? null : t;
    setDrawMode('simple_select'); // deactivate draw tool
    setActiveAnnotationTool(next);
  };

  return (
    <>
      {/* Photoshop-style vertical tray — bottom-left, above scale control */}
      <div className="absolute bottom-16 left-4 z-30 flex flex-col gap-1 bg-gray-900 rounded-xl shadow-2xl p-2 border border-gray-700">
        <p className="text-gray-500 text-[10px] font-medium uppercase tracking-widest text-center px-1 pb-1 border-b border-gray-700">Draw</p>

        <TrayButton active={mode === 'draw_polygon'} onClick={() => handleDrawTool('draw_polygon')} title="Polygon">
          ⬡
        </TrayButton>
        <TrayButton active={mode === 'draw_rectangle'} onClick={() => handleDrawTool('draw_rectangle')} title="Rectangle">
          ▭
        </TrayButton>
        <TrayButton active={mode === 'draw_circle'} onClick={() => handleDrawTool('draw_circle')} title="Circle">
          ◯
        </TrayButton>

        <div className="h-px bg-gray-700 my-0.5" />

        <TrayButton active={mode === 'simple_select' && !activeAnnotationTool} onClick={() => handleDrawTool('simple_select')} title="Select / move">
          ↖
        </TrayButton>

        {pendingFeatures.length > 0 && (
          <>
            <div className="h-px bg-gray-700 my-0.5" />
            <TrayButton active={false} onClick={() => setSaveOpen(true)} title={`Save ${pendingFeatures.length} shape(s)`}>
              💾
            </TrayButton>
            <TrayButton active={false} onClick={clearAll} title="Clear all shapes">
              🗑
            </TrayButton>
          </>
        )}

        {/* Stamp / annotation tools */}
        <div className="h-px bg-gray-700 my-0.5" />
        <p className="text-gray-500 text-[10px] font-medium uppercase tracking-widest text-center px-1 pb-1 border-b border-gray-700">Stamp</p>

        {ANNOTATION_TOOLS.map(tool => (
          <TrayButton
            key={tool.type}
            active={activeAnnotationTool === tool.type}
            onClick={() => handleStampTool(tool.type)}
            title={tool.label}
            squircle={tool.squircle}
          >
            {tool.icon}
          </TrayButton>
        ))}

        {activeDay && (
          <>
            <div className="h-px bg-gray-700 my-0.5" />
            <TrayButton active={false} onClick={handleShare} title={sharing ? 'Copying…' : 'Share map link'}>
              🔗
            </TrayButton>
          </>
        )}
      </div>

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save drawing" size="sm">
        <div className="space-y-4">
          <Input label="Drawing name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Restricted area" />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Color</label>
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="h-9 w-full rounded border border-gray-300 cursor-pointer" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} loading={saving} className="flex-1">Save drawing</Button>
            <Button variant="secondary" onClick={() => setSaveOpen(false)} className="flex-1">Cancel</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function TrayButton({ active, onClick, title, children, squircle }: {
  active: boolean; onClick: () => void; title: string; children: React.ReactNode; squircle?: string;
}) {
  if (squircle) {
    return (
      <button
        onClick={onClick}
        title={title}
        className={`w-9 h-9 rounded-lg transition-colors flex items-center justify-center ${active ? 'ring-2 ring-white' : 'hover:opacity-80'}`}
      >
        <div style={{ width: 26, height: 26, background: squircle, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, color: 'white', fontFamily: 'system-ui,sans-serif' }}>
          {children}
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-9 h-9 rounded-lg text-lg font-bold transition-colors flex items-center justify-center
        ${active ? 'bg-brand-500 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
    >
      {children}
    </button>
  );
}
